import { useState } from "react";
import {
    Button,
    Card,
    DatePicker,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Input,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
} from "@jhool-io/fe-components";
import { format } from "date-fns";
import { useParams, useSearchParams } from "react-router-dom";
import ListState from "../../../../components/ListState/ListState";
import { useAppSelector } from "../../../../hooks/useRedux";
import { useGetPrescriptions } from "../../hooks/e-prescribe.queries";
import Loader from "../../../../shared-ui/Loader/Loader";
import PrescriptionListTable from "./PrescriptionListTable";
import useToast from "../../../../hooks/useToast";
import {
    ISSOLaunchResponse,
    PrescriptionEventStatues,
} from "../../types/e-prescribe.types";
import { useTriggerSSOLaunch } from "../../hooks/e-prescribe.mutations";
import {
    canStringBeNumber,
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import { getFilterButtonText } from "../../helpers/e-prescribe.helpers";
import DrFirstIframe from "../DrFirstIframe/DrFirstIframe";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import { useDebounce } from "../../../../hooks/helpers";
import { useFetchProviders } from "../../../providers/hooks/providers.queries";
import CheckIcon from "../../../../icons/Check";
import { useDoesUserHaveRole } from "../../../../hooks/permissions";
import { UserRole } from "../../../user-management/types/user-management.types";

interface PrescriptionListProps {
    sessionHistoryId?: string;
}

export default function PrescriptionList({
    sessionHistoryId,
}: PrescriptionListProps) {
    const [launchData, setLaunchData] = useState<ISSOLaunchResponse | null>(
        null
    );
    const [providerNameSearchString, setProviderNameSearchString] =
        useState("");
    const [showProvidersPopover, setShowProvidersPopover] = useState(false);

    const { practice } = useAppSelector((state) => state.userPractice);

    const params = useParams();
    const { clientId } = params;
    const [searchParams, setSearchParams] = useSearchParams();

    const isUserAuditor = useDoesUserHaveRole([
        UserRole.AUDITOR,
        UserRole.AUDITOR_ADMIN,
    ]);

    const statusFilter = searchParams.get("status") ?? "";
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;
    const pageFilter = Number(searchParams.get("page")) || 0;
    const createdDateFromFilter = searchParams.get("created_date_from");
    const createdDateToFilter = searchParams.get("created_date_to");
    const signedDateFromFilter = searchParams.get("signed_date_from");
    const signedDateToFilter = searchParams.get("signed_date_to");
    const completedDateFromFilter = searchParams.get("completed_date_from");
    const completedDateToFilter = searchParams.get("completed_date_to");
    const providerIdFilter = searchParams.get("provider") ?? "";
    const searchFilter = searchParams.get("search_string") ?? "";
    const quantityFromFilter = searchParams.get("quantity_from") ?? "";
    const quantityToFilter = searchParams.get("quantity_to") ?? "";

    const debouncedProviderNameSearchString = useDebounce(
        providerNameSearchString
    );
    const debouncedSearchFilter = useDebounce(searchFilter);
    const debouncedQuantityFromFilter = useDebounce(quantityFromFilter);
    const debouncedQuantityToFilter = useDebounce(quantityToFilter);

    const triggerSSOLaunch = useTriggerSSOLaunch();

    const { data, isLoading, error, isSuccess } = useGetPrescriptions({
        practiceId: practice?.practice_id ?? "",
        filters: {
            client_id: clientId,
            limit: limitFilter,
            page: pageFilter,
            session_history_id: sessionHistoryId,
            created_date_from: createdDateFromFilter,
            created_date_to: createdDateToFilter,
            signed_date_from: signedDateFromFilter,
            signed_date_to: signedDateToFilter,
            completed_date_from: completedDateFromFilter,
            completed_date_to: completedDateToFilter,
            status: statusFilter,
            provider_id: isUserAuditor ? providerIdFilter : undefined,
            search_string: debouncedSearchFilter,
            quantity_from: debouncedQuantityFromFilter,
            quantity_to: debouncedQuantityToFilter,
        },
    });
    const providers = useFetchProviders(
        {
            search_string: debouncedProviderNameSearchString,
            page: 1,
            limit: 20,
        },
        isUserAuditor
    );

    const { toast } = useToast();

    const createdDateFromFilterToDate = convertDateFilterStringToDate(
        createdDateFromFilter
    );
    const createdDateToFilterToDate =
        convertDateFilterStringToDate(createdDateToFilter);
    const signedDateFromFilterToDate =
        convertDateFilterStringToDate(signedDateFromFilter);
    const signedDateToFilterToDate =
        convertDateFilterStringToDate(signedDateToFilter);
    const completedDateFromFilterToDate = convertDateFilterStringToDate(
        completedDateFromFilter
    );
    const completedDateToFilterToDate = convertDateFilterStringToDate(
        completedDateToFilter
    );

    const providerNameToDisplay = providers?.data?.data?.find(
        (provider) => provider.provider_id === providerIdFilter
    )?.provider_name;

    const actionButtonText = () => {
        if (isLoading) return "Loading...";
        if (isSuccess && data.data.length === 0) return "Add Prescription";
        if (isSuccess && data.data.length === 1) return "Update Prescription";
        return "Update Prescriptions";
    };

    const handleFilterChange = (paramName: string, value: string) => {
        if (value === "All") searchParams.delete(paramName);
        else {
            searchParams.set(paramName, value);
        }
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleDatesFilterChange = (paramName: string, date: Date | null) => {
        if (date === null) searchParams.delete(paramName);
        else searchParams.set(paramName, format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleInputFilterChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        paramName: string
    ) => {
        if (e.target.value === "") searchParams.delete(paramName);
        else searchParams.set(paramName, e.target.value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleTriggerSSOLaunch = async () => {
        triggerSSOLaunch.mutate(
            {
                practiceId: practice?.practice_id ?? "",
                payload: {
                    landing_screen: "report",
                    session_history_id: sessionHistoryId,
                    client_id: clientId ?? "",
                },
            },

            {
                onSuccess: (res) => {
                    setLaunchData(res.data);
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err?.response?.data?.message ??
                            "Cannot update prescriptions at this time",
                    });
                },
            }
        );
    };

    return (
        <>
            <DrFirstIframe
                launchData={launchData}
                onClose={() => setLaunchData(null)}
                title="Update Prescriptions"
                clientId={clientId ?? ""}
                practiceId={practice?.practice_id ?? ""}
            />

            <div>
                <Card classNames="mb-[--spacing-header]">
                    <div className="flex flex-col gap-y-[--spacing-items]">
                        <SearchInput
                            placeholder="Search by client name or chart number"
                            containerClass="w-[400px] max-w-full"
                            onChange={(e) =>
                                handleInputFilterChange(e, "search_string")
                            }
                            defaultValue={searchFilter}
                        />
                        <div className="flex items-center justify-between gap-[--spacing-items] flex-wrap">
                            <div className="flex items-center gap-[--spacing-items] flex-wrap">
                                {isUserAuditor && (
                                    <Popover
                                        open={showProvidersPopover}
                                        onOpenChange={(open) => {
                                            setShowProvidersPopover(open);
                                        }}
                                    >
                                        <PopoverTrigger
                                            asChild
                                            onClick={() =>
                                                setShowProvidersPopover(true)
                                            }
                                        >
                                            <FilterButton
                                                className={
                                                    providerIdFilter
                                                        ? "capitalize"
                                                        : "normal-case"
                                                }
                                            >
                                                {providerNameToDisplay
                                                    ? truncateString(
                                                          providerNameToDisplay
                                                      )
                                                    : "Provider"}
                                            </FilterButton>
                                        </PopoverTrigger>
                                        <PopoverContent align="start">
                                            <SearchInput
                                                placeholder="Search by name"
                                                onChange={(e) =>
                                                    setProviderNameSearchString(
                                                        e.target.value
                                                    )
                                                }
                                            />

                                            <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                                                {providers.isLoading ? (
                                                    <Loader wrapperClassName="h-[150px]" />
                                                ) : null}
                                                {providers.error && (
                                                    <ListState
                                                        isError
                                                        context="general"
                                                        stateHelperText="Please try again later"
                                                        errorMsg={
                                                            providers.error
                                                                ?.response?.data
                                                                ?.message ||
                                                            "Error loading providers"
                                                        }
                                                        cardClass="h-[150px]"
                                                    />
                                                )}
                                                {providers.data?.data &&
                                                    providers.isSuccess && (
                                                        <>
                                                            <Button
                                                                size="auto"
                                                                variant="can-hover"
                                                                className="font-medium capitalize w-full justify-between shrink-0 mt-2"
                                                                onClick={() => {
                                                                    handleFilterChange(
                                                                        "provider",
                                                                        "All"
                                                                    );
                                                                    setShowProvidersPopover(
                                                                        false
                                                                    );
                                                                    setProviderNameSearchString(
                                                                        ""
                                                                    );
                                                                }}
                                                            >
                                                                All
                                                                {!providerIdFilter ? (
                                                                    <CheckIcon className="stroke-primary" />
                                                                ) : null}
                                                            </Button>
                                                            {providers?.data?.data.map(
                                                                (provider) => (
                                                                    <Button
                                                                        key={
                                                                            provider.provider_id
                                                                        }
                                                                        size="auto"
                                                                        variant="can-hover"
                                                                        className="font-medium capitalize w-full justify-between shrink-0"
                                                                        onClick={() => {
                                                                            handleFilterChange(
                                                                                "provider",
                                                                                provider.provider_id
                                                                            );

                                                                            setShowProvidersPopover(
                                                                                false
                                                                            );
                                                                            setProviderNameSearchString(
                                                                                ""
                                                                            );
                                                                        }}
                                                                    >
                                                                        {displayNameInRightFormat(
                                                                            provider.provider_name
                                                                        )}
                                                                        {providerIdFilter ===
                                                                        provider.provider_id ? (
                                                                            <CheckIcon className="stroke-primary" />
                                                                        ) : null}
                                                                    </Button>
                                                                )
                                                            )}
                                                        </>
                                                    )}
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                )}
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <FilterButton>Quantity</FilterButton>
                                    </PopoverTrigger>
                                    <PopoverContent align="start">
                                        <div className="flex flex-col gap-y-2">
                                            <Input
                                                type="number"
                                                placeholder="From"
                                                className="w-[100px]"
                                                defaultValue={
                                                    quantityFromFilter
                                                }
                                                max={
                                                    quantityToFilter &&
                                                    canStringBeNumber(
                                                        quantityToFilter
                                                    )
                                                        ? Number(
                                                              quantityToFilter
                                                          )
                                                        : undefined
                                                }
                                                min={1}
                                                onChange={(e) =>
                                                    handleInputFilterChange(
                                                        e,
                                                        "quantity_from"
                                                    )
                                                }
                                            />
                                            <Input
                                                type="number"
                                                placeholder="To"
                                                className="w-[100px]"
                                                defaultValue={quantityToFilter}
                                                min={
                                                    quantityFromFilter &&
                                                    canStringBeNumber(
                                                        quantityFromFilter
                                                    )
                                                        ? Number(
                                                              quantityFromFilter
                                                          )
                                                        : 1
                                                }
                                                onChange={(e) =>
                                                    handleInputFilterChange(
                                                        e,
                                                        "quantity_to"
                                                    )
                                                }
                                            />
                                        </div>
                                    </PopoverContent>
                                </Popover>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {getFilterButtonText(
                                                "Status",
                                                statusFilter
                                            )}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent width="250px">
                                        <DropdownItem
                                            onClick={() =>
                                                handleFilterChange(
                                                    "status",
                                                    "All"
                                                )
                                            }
                                            isSelected={!statusFilter}
                                        >
                                            All
                                        </DropdownItem>
                                        {Object.values(
                                            PrescriptionEventStatues
                                        ).map((status) => (
                                            <DropdownItem
                                                key={status}
                                                onClick={() =>
                                                    handleFilterChange(
                                                        "status",
                                                        status
                                                    )
                                                }
                                                isSelected={
                                                    status === statusFilter
                                                }
                                            >
                                                {removeEnumUnderscore(status)}
                                            </DropdownItem>
                                        ))}
                                    </DropdownContent>
                                </Dropdown>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <FilterButton>
                                            Date Created
                                        </FilterButton>
                                    </PopoverTrigger>
                                    <PopoverContent align="end">
                                        <div className="mb-1">
                                            <DatePicker
                                                label="From"
                                                placeholderText="MM/DD/YYYY"
                                                selected={
                                                    createdDateFromFilterToDate
                                                }
                                                onChange={(date) =>
                                                    handleDatesFilterChange(
                                                        "created_date_from",
                                                        date
                                                    )
                                                }
                                                maxDate={
                                                    createdDateToFilterToDate ||
                                                    new Date(Date.now())
                                                }
                                                isClearable
                                            />
                                        </div>
                                        <DatePicker
                                            label="To"
                                            placeholderText="MM/DD/YYYY"
                                            selected={createdDateToFilterToDate}
                                            onChange={(date) =>
                                                handleDatesFilterChange(
                                                    "created_date_to",
                                                    date
                                                )
                                            }
                                            minDate={
                                                createdDateFromFilterToDate
                                            }
                                            isClearable
                                        />
                                    </PopoverContent>
                                </Popover>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <FilterButton>Signed Date</FilterButton>
                                    </PopoverTrigger>
                                    <PopoverContent align="end">
                                        <div className="mb-1">
                                            <DatePicker
                                                label="From"
                                                placeholderText="MM/DD/YYYY"
                                                selected={
                                                    signedDateFromFilterToDate
                                                }
                                                onChange={(date) =>
                                                    handleDatesFilterChange(
                                                        "signed_date_from",
                                                        date
                                                    )
                                                }
                                                maxDate={
                                                    signedDateToFilterToDate ||
                                                    new Date(Date.now())
                                                }
                                                isClearable
                                            />
                                        </div>
                                        <DatePicker
                                            label="To"
                                            placeholderText="MM/DD/YYYY"
                                            selected={signedDateToFilterToDate}
                                            onChange={(date) =>
                                                handleDatesFilterChange(
                                                    "signed_date_to",
                                                    date
                                                )
                                            }
                                            minDate={signedDateFromFilterToDate}
                                            isClearable
                                        />
                                    </PopoverContent>
                                </Popover>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <FilterButton>
                                            Date Completed
                                        </FilterButton>
                                    </PopoverTrigger>
                                    <PopoverContent align="end">
                                        <div className="mb-1">
                                            <DatePicker
                                                label="From"
                                                placeholderText="MM/DD/YYYY"
                                                selected={
                                                    completedDateFromFilterToDate
                                                }
                                                onChange={(date) =>
                                                    handleDatesFilterChange(
                                                        "completed_date_from",
                                                        date
                                                    )
                                                }
                                                maxDate={
                                                    completedDateToFilterToDate ||
                                                    new Date(Date.now())
                                                }
                                                isClearable
                                            />
                                        </div>
                                        <DatePicker
                                            label="To"
                                            placeholderText="MM/DD/YYYY"
                                            selected={
                                                completedDateToFilterToDate
                                            }
                                            onChange={(date) =>
                                                handleDatesFilterChange(
                                                    "completed_date_to",
                                                    date
                                                )
                                            }
                                            minDate={
                                                completedDateFromFilterToDate
                                            }
                                            isClearable
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            {clientId ? (
                                <div>
                                    <Button
                                        disabled={triggerSSOLaunch.isLoading}
                                        onClick={handleTriggerSSOLaunch}
                                    >
                                        {actionButtonText()}
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </Card>
                {isLoading && <Loader />}
                {error && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error?.response?.data.message ||
                            `Cannot display prescriptions`
                        }
                    />
                )}
                {isSuccess && data.data.length === 0 && (
                    <ListState
                        isError={false}
                        stateHelperText="Prescriptions will appear here when available"
                        emptyMessage="No prescriptions to display"
                    />
                )}
                {isSuccess && data.data.length > 0 && (
                    <PrescriptionListTable
                        data={data.data}
                        hideNoteData={Boolean(sessionHistoryId)}
                        sessionHistoryId={sessionHistoryId}
                    />
                )}
            </div>
        </>
    );
}
